import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { StackSimple } from "@phosphor-icons/react";

export function InstructDialog({ open, onOpenChange, quoteId, quoteName }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("default");
  const [instructing, setInstructing] = useState(false);

  useEffect(() => {
    if (open) {
      API.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
    }
  }, [open]);

  const handleInstruct = async () => {
    if (instructing) return;
    setInstructing(true);
    try {
      const params = selectedTemplateId && selectedTemplateId !== "default"
        ? `?template_id=${selectedTemplateId}` : "";
      const { data } = await API.post(`/quotes/${quoteId}/instruct${params}`);
      onOpenChange(false);
      // Sale-and-purchase quotes return TWO cases (one sale, one purchase)
      // — navigate to the purchase leg by default and toast about the pair.
      if (data.split && Array.isArray(data.cases) && data.cases.length > 1) {
        const purchase = data.cases.find((c) => c.transaction_leg === "purchase") || data.cases[1];
        toast.success(`2 cases created — Sale + Purchase. Opening Purchase case…`);
        navigate(`/cases/${purchase.id}`);
      } else if (data.case) {
        toast.success("Case created from quote");
        navigate(`/cases/${data.case.id}`);
      } else if (data.cases && data.cases[0]) {
        toast.success("Case created from quote");
        navigate(`/cases/${data.cases[0].id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to instruct");
    } finally {
      setInstructing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Instruct — Open New Case</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {quoteName && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm font-medium text-emerald-800">{quoteName}</p>
              <p className="text-xs text-emerald-600 mt-0.5">A new case will be created from this quote</p>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Workflow Template</label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger data-testid="instruct-template-select">
                <SelectValue placeholder="Choose workflow..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default (6-stage UK conveyancing)</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="flex items-center gap-2">
                      <StackSimple weight="fill" className="w-3.5 h-3.5 text-slate-400 inline" />
                      {t.name}
                      <span className="text-xs text-slate-400">({t.stages?.length || 0} stages)</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTemplateId && selectedTemplateId !== "default" && (() => {
              const tmpl = templates.find((t) => t.id === selectedTemplateId);
              if (!tmpl) return null;
              return (
                <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs" data-testid="instruct-template-preview">
                  {tmpl.description && <p className="text-slate-500 mb-1.5">{tmpl.description}</p>}
                  <div className="flex flex-wrap gap-1">
                    {tmpl.stages?.map((s) => (
                      <span key={s.key} className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600 text-[10px] font-medium">
                        {s.label}
                        {s.subcategories?.length > 0 && <span className="text-slate-400 ml-0.5">({s.subcategories.length})</span>}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
          <Button onClick={handleInstruct} disabled={instructing}
            data-testid="confirm-instruct-btn"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
            {instructing ? "Creating Case..." : "Instruct & Open Case"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
